import { useState } from 'react';
import {
  Delete,
  Edit,
  FileCopy,
  Dvr,
  LinkOutlined,
  CloudDownload,
  Visibility,
} from '@mui/icons-material';
import TextField from 'components/FormElements/AppTextInput';
import Table, { TableActions } from 'components/Table';
import PageContainer from 'components/PageContainer';
import { fundStatusMap } from 'adminConstants/fundProperties';
import {
  createTrancheEditRoute,
  TRANCHE_ADD,
  TRANCHE_BESPOKE_LINK,
} from 'adminConstants/routes';
import { MuiDateRangePicker } from 'components/FormElements/DateRangePicker';
import AlertDialog from 'components/AlertDialog';
import { FirmSelector } from 'components/FirmSelector';
import AppAutocomplete from 'components/FormElements/AppAutocomplete';
import PageContent from 'components/PageContent';
import PageFilters, { Filter } from 'components/PageFilters';
import SearchField from 'components/SearchField';
import ExportDropdownMenu from 'components/ExportDropdownMenu';
import {
  numberToCurrencyString,
  dateToLabel,
  getDateRange,
} from 'further-ui/utils';
import { Column } from 'components/Table/types';
import { useDisclosure } from 'further-ui/hooks';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import { RolePermission } from 'further-types/admin-user-role';
import { useFund } from 'hooks/data/fund/useFund';
import trpc from 'lib/trpcClient';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { useNotification } from 'hooks/ui/useNotification';
import { createTrancheOnboardingLink } from 'helpers/trancheLink/trancheLink';
import { Tranche, TrancheListProps } from './types';

const breadcrumbs = [
  { label: 'Dashboard', link: '/' },
  { label: 'Tranches', isActive: true },
];

const TrancheList = ({
  isSuperAdmin,
  filters,
  handleChangeFilters,
  fundStatus,
  tags,
  isExportLoading,
  handleExportFunds,
  handlePerformanceFeeSimulation,
  pagination,
  tranches,
}: TrancheListProps) => {
  const [createPermission, deletePermission, updatePermission] =
    useHasPermissions([
      RolePermission.CreateTranche,
      RolePermission.DeleteTranche,
      RolePermission.EditTranche,
    ]);
  const [copyFlag, setCopyFlag] = useState(false);
  const deleteDisclosure = useDisclosure();
  const { duplicate, exportFund } = useFund();
  const deleteFund = trpc.fund.delete.useMutation(
    useHandleTrpcMutation()({
      successMessage: 'Tranche deleted successfully.',
      invalidations: [['tranches']],
      onSuccess: () => {
        if (tranches?.data?.length === 1) {
          pagination.pageBack();
        }
        deleteDisclosure.onClose();
      },
    }),
  );
  const notification = useNotification();
  const quickRanges = setDefinedRanges();

  const columns: Array<Column<Tranche>> = [
    {
      label: 'Firm name',
      key: 'firmId',
      render: (elm) => elm?.firmId?.firmName,
    },
    {
      label: 'Tranche',
      key: 'fundName',
      render: (elm) => elm.fundName,
    },
    {
      label: 'Type',
      key: 'type',
      sort: false,
      render: (elm) => elm?.tag?.name,
    },
    {
      label: 'First subscription date',
      key: 'firstInvestmentDate',
      render: (elm) => dateToLabel(elm.firstInvestmentDate),
    },
    {
      label: 'Close date',
      key: 'closedDate',
      render: (elm) => elm.closedDate && dateToLabel(elm.closedDate),
    },
    {
      label: 'Investors',
      key: 'investorCount',
      sort: false,
      render: (elm) => String(elm.investorCount),
    },
    {
      label: 'Total subscription value',
      key: 'totalInvestment',
      sort: false,
      render: (elm) => numberToCurrencyString(elm.totalInvestment),
    },
    {
      label: 'Status',
      key: 'fundStatus',
      render: (elm) =>
        elm.fundStatus === 1
          ? 'Open'
          : elm.fundStatus === 0
          ? 'Closed'
          : 'Draft',
    },
    {
      label: 'Actions',
      key: 'actions',
      sort: false,
      render: (elm) => (
        <TableActions
          showAsDropdown
          actions={[
            {
              visible: updatePermission,
              link: createTrancheEditRoute(elm._id),
              label: 'Edit',
              icon: Edit,
              color: 'primary',
            },
            {
              visible: !updatePermission,
              link: createTrancheEditRoute(elm._id),
              label: 'View',
              icon: Visibility,
              color: 'primary',
            },
            {
              visible: createPermission,
              label: 'Duplicate',
              icon: FileCopy,
              color: 'primary',
              onClick: () => {
                setCopyFlag(!copyFlag);
                duplicate.mutate(elm._id);
              },
            },
            {
              label: 'Download subscription data',
              icon: CloudDownload,
              color: 'primary',
              onClick: () =>
                exportFund(
                  elm?._id,
                  elm?.fundName,
                  elm?.firmId?.firmName ?? '',
                ),
            },
            {
              visible:
                !!elm?.firmId?.domain && elm.fundStatus === fundStatusMap.open,
              label: 'Make custom subscription link',
              icon: LinkOutlined,
              color: 'primary',
              link: {
                pathname: TRANCHE_BESPOKE_LINK,
                state: { trancheId: elm._id },
              },
            },
            {
              visible:
                !!elm?.firmId?.domain && elm.fundStatus === fundStatusMap.open,
              label: 'Copy tranche URL to clipboard',
              icon: Dvr,
              color: 'primary',
              onClick: () => {
                navigator.clipboard.writeText(createTrancheOnboardingLink(elm));
                notification.success('Tranche link copied to clipboard.');
              },
            },
            {
              visible: deletePermission,
              label: 'Delete',
              icon: Delete,
              color: 'error',
              onClick: () => deleteDisclosure.stageAction(elm),
            },
          ]}
        />
      ),
    },
  ];

  const handleConfirmDelete = () => {
    const { _id } = deleteDisclosure.actionProps || {};
    if (!_id) return;
    deleteFund.mutate({ id: _id });
  };

  return (
    <PageContainer heading="Tranches" breadcrumbs={breadcrumbs}>
      <PageContent>
        <PageFilters
          pageId="tranches-table"
          buttons={[
            {
              visible: createPermission,
              link: TRANCHE_ADD,
              label: 'Add Tranche',
            },
          ]}
          exportComponent={
            <ExportDropdownMenu
              title="Export"
              loading={isExportLoading}
              actions={[
                {
                  label: 'Tranche data',
                  onClick: handleExportFunds,
                  disabled: isExportLoading,
                },
                {
                  label: 'Performance fee simulation',
                  onClick: handlePerformanceFeeSimulation,
                  disabled: isExportLoading,
                },
              ]}
            />
          }
        >
          {isSuperAdmin && (
            <Filter>
              <FirmSelector
                firmId={filters.firmId}
                placeholder="Select firm"
                onChange={(firmId) => {
                  handleChangeFilters({ firmId });
                }}
              />
            </Filter>
          )}
          <Filter>
            <SearchField
              fullWidth
              withoutIcon
              variant="outlined"
              placeholder="Search by tranche name"
              value={filters.fundName}
              onSearch={(value) => handleChangeFilters({ fundName: value })}
              autoSearchOnDebounce
              allowEmptyQuery
            />
          </Filter>
          <Filter>
            <AppAutocomplete
              name="tagId"
              options={tags?.data ?? []}
              getOptionLabel={(option) => option?.name}
              getOptionKey={(option) => option._id}
              filterSelectedOptions
              value={filters.tagId}
              onChange={(_, newValue) => {
                handleChangeFilters({ tagId: newValue });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder="Fund type"
                />
              )}
            />
          </Filter>
          <Filter>
            <AppAutocomplete
              id="status"
              options={fundStatus?.reverse()}
              getOptionLabel={(option) => option.name}
              getOptionKey={(option) => option.value}
              value={filters.fundStatus}
              filterSelectedOptions
              onChange={(_, newValue) => {
                handleChangeFilters({ fundStatus: newValue });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder="Select tranche status"
                />
              )}
            />
          </Filter>
          <Filter>
            <MuiDateRangePicker
              dateRange={{
                startDate: filters.dateRange?.startDate.startOfDay(),
                endDate: filters.dateRange?.endDate.startOfDay(),
              }}
              onDateRangeChange={(data) => {
                handleChangeFilters({ dateRange: getDateRange(data) });
              }}
              placeholder="Search by close date"
              definedRanges={quickRanges}
            />
          </Filter>
        </PageFilters>

        <Table
          columns={columns}
          onRequestSort={pagination.handleRequestSort}
          order={pagination.order}
          orderBy={pagination.orderBy}
          tablebody={tranches.data || []}
          onPageChange={pagination.handleChangePage}
          onRowsPerPageChange={pagination.handleChangeRowsPerPage}
          page={pagination.page}
          rowsPerPage={pagination.rowsPerPage}
          count={tranches.total}
          pagination
          loading={tranches.isFetching}
          variant="nohover"
        />

        <AlertDialog
          open={deleteDisclosure.isOpen}
          onClose={deleteDisclosure.onClose}
          onConfirm={handleConfirmDelete}
          title="Are you sure you want to delete this tranche?"
          content={
            <p>
              This cannot be undone and all information relating to the tranche
              will be lost.
            </p>
          }
          btnLabels={{
            cancel: 'Go back',
            confirm: 'Delete tranche',
          }}
        />
      </PageContent>
    </PageContainer>
  );
};

export default TrancheList;

function setDefinedRanges() {
  const currentYear = new Date().getFullYear();
  const ranges: Array<{ label: string; startDate: Date; endDate: Date }> = [];

  for (let i = 0; i < 7; i++) {
    const year = currentYear - i;
    ranges.push({
      label: `${year}`,
      startDate: new Date(`${year}-01-01`),
      endDate: new Date(`${year}-12-31`),
    });
  }

  return ranges;
}
